import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { publicApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowRight, Shield, Wrench, Zap } from "lucide-react";

const defaultAboutHtml = `<p>Dryice Marketplace is an independent equipment marketplace offering new and used dry ice blasting systems, air compressors, pelletizers, and accessories from leading manufacturers including <strong>ICS</strong>, <strong>IceTech</strong>, <strong>ICEsonic</strong>, <strong>Nu-Ice</strong>, <strong>Kaeser</strong>, and <strong>Cold Jet</strong>.</p>
<p>Sales and service support is provided in partnership with <strong>Dry Ice Bulls</strong>. Together, we help businesses find the right machine for their application and budget — from entry-level detailing units to heavy-duty industrial systems.</p>
<p>We are brand agnostic and focused on one thing: matching our customers with the best equipment for their needs. Whether you're starting a mobile detailing business, upgrading your manufacturing maintenance program, or building a full dry ice blasting operation — we're here to guide you every step of the way.</p>`;

export default function AboutPage() {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        publicApi.getSettings().then(res => {
            setContent(res.data.about_page_content || null);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const aboutHtml = content || defaultAboutHtml;

    return (
        <div className="min-h-[70vh]" data-testid="about-page">
            <div className="bg-card border-b border-border">
                <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-20">
                    <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Who We Are</p>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-foreground tracking-tight mb-4">
                        About Us
                    </h1>
                </div>
            </div>

            <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-20 max-w-4xl">
                <div className="space-y-10">
                    {/* Dynamic content from CMS */}
                    <div
                        className="prose prose-invert prose-orange max-w-none
                            prose-headings:font-heading prose-headings:tracking-tight
                            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                            prose-strong:text-foreground
                            prose-p:text-muted-foreground prose-p:text-base prose-p:leading-relaxed
                            prose-li:text-muted-foreground"
                        dangerouslySetInnerHTML={{ __html: aboutHtml }}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-card border border-border p-6">
                            <Shield className="h-8 w-8 text-primary mb-4" />
                            <h3 className="font-heading text-base font-bold text-foreground mb-2">Multi-Brand Marketplace</h3>
                            <p className="text-muted-foreground text-sm">We carry equipment from multiple top manufacturers so you can compare and choose the best fit — not just one brand's lineup.</p>
                        </div>
                        <div className="bg-card border border-border p-6">
                            <Wrench className="h-8 w-8 text-primary mb-4" />
                            <h3 className="font-heading text-base font-bold text-foreground mb-2">Sales & Service Support</h3>
                            <p className="text-muted-foreground text-sm">From initial consultation to after-sale support, our team is with you for the long run.</p>
                        </div>
                        <div className="bg-card border border-border p-6">
                            <Zap className="h-8 w-8 text-primary mb-4" />
                            <h3 className="font-heading text-base font-bold text-foreground mb-2">New & Used Equipment</h3>
                            <p className="text-muted-foreground text-sm">Quality-checked pre-owned machines alongside the latest new equipment. Options for every budget and application.</p>
                        </div>
                    </div>

                    <div className="bg-card border border-primary/30 p-8 text-center">
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">Ready to Find the Right Machine?</h2>
                        <p className="text-muted-foreground text-sm mb-6">Talk to a Dry Ice Cleaning Specialist and get expert guidance tailored to your application.</p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/products">
                                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-8">
                                    Browse Equipment <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </Link>
                            <Link to="/guidance">
                                <Button variant="outline" className="rounded-none uppercase font-bold tracking-wider px-8 border-2">
                                    Get Guidance
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
